import { DISTURBANCE_TYPES, MONTH_LIST, RDNaturalDisturbanceEvent } from '@cibo/core'
import { NumberInputField, SelectField } from '@cibo/ui'
import ClearIcon from '@mui/icons-material/Clear'
import EditRounded from '@mui/icons-material/EditRounded'
import {
  Button,
  Collapse,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { Formik, FormikHelpers, useField } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { EventComponentProps } from '../EventDetail/EventDetailTypes'
import { useIsRequired } from '../useIsRequired'

const NaturalDisturbanceEventValidationSchema = ({ t }: any) =>
  Yup.object().shape({
    month: Yup.string().required(t('required', { context: 'month' })),
    disturbance: Yup.string().required(t('required', { context: 'disturbance' })),
    lossPer: Yup.number()
      .required(t('required', { context: 'lossPer' }))
      .min(0)
      .max(100)
      .typeError(t('required', { context: 'lossPer' })),
  })

export const NaturalDisturbanceEventEditorSimple = ({
  editing,
  year,
  name,
  onEditEvent,
  onPressRemove,
  requirement,
  isImmutable,
}: EventComponentProps) => {
  const { t } = useTranslation('@cibo/landmanager/NaturalDisturbance')
  const [{ value }, { initialValue, touched }, { setValue, setTouched }] =
    useField<RDNaturalDisturbanceEvent>(name)
  const [localEditing, setLocalEditing] = useState(!initialValue)
  const isRequired = useIsRequired(requirement)

  useEffect(() => {
    onEditEvent(!initialValue)
  }, [!initialValue])

  const validationSchema = NaturalDisturbanceEventValidationSchema({ t })

  const handleSubmit = (
    values: RDNaturalDisturbanceEvent,
    { setSubmitting }: FormikHelpers<RDNaturalDisturbanceEvent>
  ) => {
    setTouched(true, false)
    setValue(values)
    setLocalEditing(false)
    setSubmitting(false)
    onEditEvent(false)
  }

  const editingDisabled = editing || isImmutable

  return (
    <>
      <Collapse in={!localEditing}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: '275px',
          }}
        >
          <Stack spacing={1}>
            <Typography>
              {value.disturbance ? t(`disturbance_${value.disturbance}`) : '--'}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              divider={<Divider orientation="vertical" flexItem />}
            >
              {value.month && (
                <Typography variant="caption">{t(`month_${value.month}`)}</Typography>
              )}
              {value.lossPer !== undefined && (
                <Typography variant="caption">{value.lossPer}%</Typography>
              )}
            </Stack>
          </Stack>
          <Stack direction="column">
            <Tooltip title={t('edit')}>
              <IconButton
                aria-label={t('editEvent') as string}
                onClick={() => {
                  setLocalEditing(true)
                  onEditEvent(true)
                }}
                size="small"
                disabled={editingDisabled}
              >
                <EditRounded />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('remove')}>
              <IconButton
                aria-label={t('removeEvent') as string}
                onClick={onPressRemove}
                size="small"
                disabled={editingDisabled}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Collapse>
      <Formik
        initialValues={
          value ||
          ({
            month: '',
            disturbance: '',
            lossPer: 0,
          } as unknown as RDNaturalDisturbanceEvent)
        }
        validationSchema={validationSchema}
        validateOnMount
        enableReinitialize
        disabled={isImmutable}
        onSubmit={handleSubmit}
      >
        {({ isValid, resetForm, submitForm, dirty }) => {
          return (
            <Collapse in={localEditing} timeout="auto" unmountOnExit>
              <Stack spacing={2}>
                <FormControl fullWidth required={isRequired('month')}>
                  <FormLabel>{t('month')}</FormLabel>
                  <SelectField
                    fieldName="month"
                    options={MONTH_LIST.map((_, index) => `${index + 1}`)}
                    renderOption={value => t(`month_${value}`)}
                  />
                </FormControl>

                <FormControl fullWidth required>
                  <FormLabel>{t('disturbance')}</FormLabel>
                  <SelectField
                    fieldName="disturbance"
                    options={DISTURBANCE_TYPES}
                    renderOption={value => t(`disturbance_${value}`)}
                  />
                </FormControl>

                <FormControl fullWidth required={isRequired('lossPer')}>
                  <FormLabel>{t('lossPer')}</FormLabel>
                  <NumberInputField
                    name={`lossPer`}
                    data-testid={`lossPer`}
                    min={0}
                    max={100}
                    unit="%"
                  />
                </FormControl>

                <Stack direction="row" spacing={1} justifyContent="flex-end" pb={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    data-testid={`${name}-cancel`}
                    onClick={() => {
                      !initialValue && !touched && onPressRemove()
                      setLocalEditing(false)
                      onEditEvent(false)
                      resetForm()
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    data-testid={`${name}-add`}
                    size="small"
                    onClick={() => submitForm()}
                    disabled={!isValid || !dirty}
                  >
                    {!initialValue ? t('add') : t('saveEvent')}
                  </Button>
                </Stack>
              </Stack>
            </Collapse>
          )
        }}
      </Formik>
    </>
  )
}
